import { Typography } from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityPickerPopover } from '@work4all/components/lib/components/entity-picker/components';
import { ServiceContractPositionKindPicker } from '@work4all/components/lib/components/entity-picker/service-contract-position-kind-picker/ServiceContractPositionKindPicker';

import { ServiceContractDefaultTypeOfBilling } from '@work4all/models/lib/Enums/ServiceContractDefaultTypeOfBilling.enum';
import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

import { settings, useSetting } from '../../../../../../settings';

import {
  PickerTargetButton,
  PickerTargetButtonProps,
} from './PickerTargetButton';

const mapToStandardKind = (value: ServiceContractPositionKind) => {
  switch (value) {
    case ServiceContractPositionKind.UEBERNEHMEN_MIT_E_PREIS_AUS_WARTUNGS_OBJEKT:
      return ServiceContractDefaultTypeOfBilling.SINGLE_PRICE;
    case ServiceContractPositionKind.UEBERNEHMEN_MIT_WARTUNGSSUMME:
      return ServiceContractDefaultTypeOfBilling.SERVICE_PRICE;
    case ServiceContractPositionKind.UEBERNEHMEN_MIT_ARTIKELPREIS:
    default:
      return ServiceContractDefaultTypeOfBilling.ARTICLE_PRICE;
  }
};

export function ServiceContractPositionKindCell(
  props: {
    value: ServiceContractPositionKind;
    onChange: (value: ServiceContractPositionKind | null) => void;
    hideIcon?: boolean;
  } & Omit<PickerTargetButtonProps, 'onChange' | 'value'>
) {
  const { value: valueProp, onChange, ...buttonProps } = props;
  const popoverRef = useRef<EntityPickerPopover>(null);

  const { t, i18n } = useTranslation();

  const serviceContractDefaultPositionKind = useSetting(
    settings.serviceContractDefaultPositionKind()
  );

  const name = useMemo(
    () =>
      i18n.exists(`SERVICE_CONTRACT_POSITION_KINDS.${valueProp}.SHORT`)
        ? t(`SERVICE_CONTRACT_POSITION_KINDS.${valueProp}.SHORT`)
        : i18n.exists(`SERVICE_CONTRACT_POSITION_KINDS.${valueProp}.FULL`)
        ? t(`SERVICE_CONTRACT_POSITION_KINDS.${valueProp}.FULL`)
        : i18n.exists(`SERVICE_CONTRACT_POSITION_KINDS.${valueProp}`)
        ? t(`SERVICE_CONTRACT_POSITION_KINDS.${valueProp}`)
        : '',
    [valueProp, t, i18n]
  );

  const value = { id: valueProp, name };

  const handleChange = (value: {
    id: ServiceContractPositionKind;
    name: string;
  }) => {
    onChange(value?.id);
  };

  const handleDefaultPositionKindChange = (
    value: ServiceContractPositionKind
  ) => {
    serviceContractDefaultPositionKind.set(mapToStandardKind(value));
  };

  const validateStandard = useCallback(
    (kind: ServiceContractPositionKind) => {
      if (
        serviceContractDefaultPositionKind.value === mapToStandardKind(kind)
      ) {
        return true;
      }
      return false;
    },
    [serviceContractDefaultPositionKind.value]
  );

  return (
    <EntityPickerPopover
      ref={popoverRef}
      picker={
        <ServiceContractPositionKindPicker
          value={value}
          multiple={false}
          onChange={handleChange}
          onStandardChange={handleDefaultPositionKindChange}
          validateStandard={validateStandard}
        />
      }
    >
      <PickerTargetButton {...buttonProps}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography style={{ marginLeft: '12px' }}>{value?.name}</Typography>
        </div>
      </PickerTargetButton>
    </EntityPickerPopover>
  );
}
